import { Eye, EyeOff, Trash2, X } from "lucide-react";
import { useSvgDiagram2D } from "./SvgDiagram2DContext";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { CheckboxField } from "../reusable/CheckboxField";
import { FillControl } from "../reusable/FillControl";
import { StrokeStyleControl } from "../reusable/StrokeStyleControl";
import { isMultiShapeSelection } from "./SvgDiagram2DMultiSelectPanel";

function num(v: string | number, fallback: number): number {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
}

/**
 * Floating inspector for the selected ellipse: edits its geometry (center,
 * radii), outline/fill, and the start-angle / sweep arc parameters. Rendered as
 * an overlay inside the viewer (mirrors the node inspector); closes itself when
 * no ellipse is selected.
 */
export function SvgDiagram2DEllipsePanel() {
    const ctx = useSvgDiagram2D();
    const {
        doc,
        selectedEllipseIds,
        setSelectedEllipseId,
        updateEllipse,
        removeEllipse,
        pushSnapshot,
    } = ctx;

    // A multi-shape selection is handled by the shared multi-select panel.
    if (isMultiShapeSelection(ctx)) return null;

    // The inspector edits the first selected ellipse (matches the node panel,
    // which edits the first selected node).
    const ellipse = doc.ellipses.find((e) => e.id === selectedEllipseIds[0]);
    if (!ellipse) return null;

    /** Snapshot before a discrete (single-commit) edit so it can be undone. */
    const edit = (partial: Parameters<typeof updateEllipse>[1]) => {
        pushSnapshot();
        updateEllipse(ellipse.id, partial);
    };

    return (
        <div className="diagram-node-panel">
            <div className="field-row" style={{ alignItems: "center", gap: 6 }}>
                <strong style={{ flex: 1, minWidth: 0 }}>
                    Ellipse properties
                </strong>
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => setSelectedEllipseId(null)}
                    title="Close panel"
                    style={{ padding: "2px 6px" }}
                >
                    <X size={14} aria-hidden />
                </button>
            </div>

            {/* --- Properties --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Properties</p>
                <label className="field-label">
                    Label
                    <input
                        type="text"
                        value={ellipse.label}
                        onChange={(e) =>
                            updateEllipse(ellipse.id, { label: e.target.value })
                        }
                    />
                </label>
                <div className="field-row field-row--two">
                    <label className="field-label">
                        Radius X
                        <DebouncedInput
                            type="number"
                            min={0}
                            step={1}
                            value={ellipse.rx}
                            onChange={(v) =>
                                edit({ rx: Math.max(0, num(v, ellipse.rx)) })
                            }
                        />
                    </label>
                    <label className="field-label">
                        Radius Y
                        <DebouncedInput
                            type="number"
                            min={0}
                            step={1}
                            value={ellipse.ry}
                            onChange={(v) =>
                                edit({ ry: Math.max(0, num(v, ellipse.ry)) })
                            }
                        />
                    </label>
                </div>
                <div className="field-row field-row--two">
                    {(["x", "y"] as const).map((axis) => (
                        <label key={axis} className="field-label">
                            {axis.toUpperCase()}
                            <DebouncedInput
                                type="number"
                                step={1}
                                value={ellipse.center[axis]}
                                onChange={(v) =>
                                    edit({
                                        center: {
                                            ...ellipse.center,
                                            [axis]: num(
                                                v,
                                                ellipse.center[axis],
                                            ),
                                        },
                                    })
                                }
                            />
                        </label>
                    ))}
                </div>
                <label className="field-label">
                    Rotation (°)
                    <DebouncedInput
                        type="number"
                        step={1}
                        value={ellipse.rotation}
                        onChange={(v) =>
                            edit({ rotation: num(v, ellipse.rotation) })
                        }
                    />
                </label>
            </div>

            {/* --- Arc --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Arc</p>
                <div className="field-row field-row--two">
                    <label className="field-label">
                        Start angle (°)
                        <DebouncedInput
                            type="number"
                            step={1}
                            value={ellipse.startAngle}
                            onChange={(v) =>
                                edit({ startAngle: num(v, ellipse.startAngle) })
                            }
                        />
                    </label>
                    <label className="field-label">
                        Sweep (%)
                        <DebouncedInput
                            type="number"
                            min={0}
                            max={100}
                            step={1}
                            value={Math.round(ellipse.sweep * 100)}
                            onChange={(v) =>
                                edit({
                                    sweep: Math.max(
                                        0,
                                        Math.min(
                                            1,
                                            num(v, ellipse.sweep * 100) / 100,
                                        ),
                                    ),
                                })
                            }
                        />
                    </label>
                </div>
                <p
                    className="path-tool-meta"
                    style={{ margin: "4px 0 0", opacity: 0.7 }}
                >
                    A sweep below 100% draws an open arc from the start angle.
                </p>
            </div>

            {/* --- Style --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Style</p>
                <StrokeStyleControl
                    strokeStyleId={ellipse.strokeStyleId}
                    strokeStyles={doc.strokeStyles}
                    onChange={(strokeStyleId) => edit({ strokeStyleId })}
                >
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Stroke
                            <input
                                type="color"
                                value={ellipse.color}
                                onChange={(e) =>
                                    updateEllipse(ellipse.id, {
                                        color: e.target.value,
                                    })
                                }
                            />
                        </label>
                        <label className="field-label">
                            Width
                            <DebouncedInput
                                type="number"
                                min={0.1}
                                step={0.1}
                                value={ellipse.strokeWidth}
                                onChange={(v) =>
                                    edit({
                                        strokeWidth: Math.max(
                                            0.1,
                                            num(v, ellipse.strokeWidth),
                                        ),
                                    })
                                }
                            />
                        </label>
                    </div>
                    <label className="field-label">
                        Dash (e.g. "4 2", empty = solid)
                        <input
                            type="text"
                            value={ellipse.dash}
                            onChange={(e) =>
                                updateEllipse(ellipse.id, {
                                    dash: e.target.value,
                                })
                            }
                        />
                    </label>
                </StrokeStyleControl>
                <div
                    className="field-row"
                    style={{ gap: 8, alignItems: "center" }}
                >
                    <FillControl
                        fill={ellipse.fill}
                        patterns={doc.patterns}
                        onChange={(fill) => edit({ fill })}
                        label="Ellipse"
                    />
                    <CheckboxField
                        checked={ellipse.visible}
                        onChange={(checked) =>
                            updateEllipse(ellipse.id, { visible: checked })
                        }
                        label={
                            <span
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 4,
                                }}
                            >
                                {ellipse.visible ? (
                                    <Eye size={14} aria-hidden />
                                ) : (
                                    <EyeOff size={14} aria-hidden />
                                )}
                                Visible
                            </span>
                        }
                        inline
                    />
                </div>
            </div>

            {/* --- Danger --- */}
            <div className="diagram-node-panel-section">
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => removeEllipse(ellipse.id)}
                    title="Delete this ellipse"
                >
                    <Trash2 size={14} aria-hidden /> Delete ellipse
                </button>
            </div>
        </div>
    );
}
